import { z } from 'zod';
import type { SkillPackageFileInfo, SkillPackageScanCoverage } from './skill-packages.js';

export const CLAUDE_VALUE_LOOP_PROFILE = 'remote-beta-versioned-skills-v1' as const;
export const CLAUDE_VALUE_LOOP_CONSENT_REVISION = 1 as const;
export const CLAUDE_VALUE_LOOP_SCOPES = [
  'skills.packages.read',
  'skills.packages.create',
  'skills.packages.publish',
  'skills.use.report',
  'skills.analysis.read',
] as const;
export type ClaudeValueLoopScope = (typeof CLAUDE_VALUE_LOOP_SCOPES)[number];
export const CLAUDE_VALUE_LOOP_TOOLS = [
  'skillspec_list_skill_versions',
  'skillspec_get_skill_package',
  'skillspec_inspect_skill_package',
  'skillspec_create_versioned_skill',
  'skillspec_publish_skill_version',
  'skillspec_record_reported_skill_use',
  'skillspec_get_skill_use_analysis',
  'skillspec_get_skill_operation_receipt',
] as const;
export type ClaudeValueLoopTool = (typeof CLAUDE_VALUE_LOOP_TOOLS)[number];
export type ClaudeValueLoopOperation =
  | 'list_versions'
  | 'get_package'
  | 'inspect_package'
  | 'create_skill'
  | 'publish_version'
  | 'record_use'
  | 'get_analysis'
  | 'get_receipt';
export const claudeValueLoopOperations: Record<ClaudeValueLoopTool, ClaudeValueLoopOperation> = {
  skillspec_list_skill_versions: 'list_versions',
  skillspec_get_skill_package: 'get_package',
  skillspec_inspect_skill_package: 'inspect_package',
  skillspec_create_versioned_skill: 'create_skill',
  skillspec_publish_skill_version: 'publish_version',
  skillspec_record_reported_skill_use: 'record_use',
  skillspec_get_skill_use_analysis: 'get_analysis',
  skillspec_get_skill_operation_receipt: 'get_receipt',
};
const id = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const uuid = z.string().uuid();
const instant = z.string().datetime({ offset: false });
const outcome = z.enum(['success', 'partial', 'failure', 'unknown']);
const context = z.enum(['writing', 'coding', 'research', 'other', 'unknown']);
const cursor = z.string().min(1).max(2048);
const packageInput = z
  .object({
    files: z
      .array(
        z
          .object({ path: z.string().min(1).max(240), contentBase64: z.string().max(1398104) })
          .strict()
      )
      .min(1)
      .max(100),
  })
  .strict();
const target = { skillId: id, expectedVersionId: id, expectedDigest: digest };
export const claudeValueLoopArgumentsSchemas = {
  list_versions: z
    .object({
      skillId: id,
      cursor: cursor.optional(),
      limit: z.number().int().min(1).max(50).optional(),
    })
    .strict(),
  get_package: z
    .object({
      skillId: id,
      versionId: id,
      digest: digest.optional(),
      cursor: cursor.optional(),
      maxBytes: z.number().int().min(1024).max(262144).optional(),
    })
    .strict(),
  inspect_package: z.discriminatedUnion('intent', [
    z.object({ intent: z.literal('create'), package: packageInput }).strict(),
    z.object({ intent: z.literal('publish'), ...target, package: packageInput }).strict(),
  ]),
  create_skill: z
    .object({
      operationKey: uuid,
      previewId: uuid,
      packageDigest: digest,
      confirmed: z.literal(true),
      package: packageInput,
    })
    .strict(),
  publish_version: z
    .object({
      operationKey: uuid,
      previewId: uuid,
      ...target,
      packageDigest: digest,
      confirmed: z.literal(true),
      package: packageInput,
    })
    .strict(),
  record_use: z
    .object({
      operationKey: uuid,
      useId: uuid,
      skillId: id,
      versionId: id,
      digest,
      reportedAt: instant.optional(),
      reportedModel: z.string().min(1).max(100).optional(),
      reportedContext: context.optional(),
      reportedOutcome: outcome.optional(),
    })
    .strict(),
  get_analysis: z
    .object({
      skillId: id,
      versionIds: z
        .array(id)
        .min(1)
        .max(20)
        .refine((v) => new Set(v).size === v.length),
      from: instant,
      to: instant,
    })
    .strict()
    .refine(
      (v) =>
        Date.parse(v.to) > Date.parse(v.from) &&
        Date.parse(v.to) - Date.parse(v.from) <= 366 * 86400000
    ),
  get_receipt: z
    .object({
      operationKey: uuid,
      operation: z.enum(['create_skill', 'publish_version', 'record_use']),
    })
    .strict(),
} as const;
export type ClaudeValueLoopArguments<O extends ClaudeValueLoopOperation> = z.infer<
  (typeof claudeValueLoopArgumentsSchemas)[O]
>;

export function assertClaudeValueLoopScopes(
  values: readonly string[]
): asserts values is readonly ClaudeValueLoopScope[] {
  if (
    !Array.isArray(values) ||
    values.length === 0 ||
    values.length > 5 ||
    new Set(values).size !== values.length ||
    values.some((v) => !(CLAUDE_VALUE_LOOP_SCOPES as readonly string[]).includes(v))
  )
    throw new Error('invalid_scope');
}
export function claudeValueLoopClientDisposition(input: {
  enabled: boolean;
  configuredClientMatches: boolean;
  profile: string;
}): 'value-loop' | 'legacy' | 'deny' {
  if (input.profile !== CLAUDE_VALUE_LOOP_PROFILE && !input.configuredClientMatches)
    return 'legacy';
  return input.enabled &&
    input.configuredClientMatches &&
    input.profile === CLAUDE_VALUE_LOOP_PROFILE
    ? 'value-loop'
    : 'deny';
}
export function requiredClaudeValueLoopScope(
  operation: ClaudeValueLoopOperation,
  args: Record<string, unknown>
): ClaudeValueLoopScope {
  if (operation === 'get_receipt')
    return requiredClaudeValueLoopScope(args.operation as ClaudeValueLoopOperation, {});
  if (operation === 'inspect_package')
    return args.intent === 'publish' ? 'skills.packages.publish' : 'skills.packages.create';
  if (operation === 'create_skill') return 'skills.packages.create';
  if (operation === 'publish_version') return 'skills.packages.publish';
  if (operation === 'record_use') return 'skills.use.report';
  if (operation === 'get_analysis') return 'skills.analysis.read';
  return 'skills.packages.read';
}
export const isClaudeValueLoopMutation = (operation: ClaudeValueLoopOperation) =>
  ['create_skill', 'publish_version', 'record_use'].includes(operation);
export function resolveClaudeValueLoopTools(input: {
  scopes: readonly string[];
  role: string;
  writesEnabled: boolean;
}): ClaudeValueLoopTool[] {
  assertClaudeValueLoopScopes(input.scopes);
  if (!['OWNER', 'ADMIN', 'MEMBER'].includes(input.role)) return [];
  return CLAUDE_VALUE_LOOP_TOOLS.filter((tool) => {
    const op = claudeValueLoopOperations[tool];
    if (op === 'get_receipt')
      return ['skills.packages.create', 'skills.packages.publish', 'skills.use.report'].some((s) =>
        input.scopes.includes(s)
      );
    if (isClaudeValueLoopMutation(op) && !input.writesEnabled) return false;
    if (op === 'inspect_package')
      return (
        input.writesEnabled &&
        ['skills.packages.create', 'skills.packages.publish'].some((s) => input.scopes.includes(s))
      );
    return input.scopes.includes(requiredClaudeValueLoopScope(op, {}));
  });
}

/** Canonical raw JSON: no coercion, inherited properties, undefined, cycles or nonfinite numbers. */
export function canonicalClaudeValueLoopJSON(value: unknown): string {
  const active = new Set<object>();
  const encode = (v: unknown): string => {
    if (v === null || typeof v === 'boolean' || typeof v === 'string') return JSON.stringify(v);
    if (typeof v === 'number' && Number.isFinite(v)) return JSON.stringify(v);
    if (!v || typeof v !== 'object' || active.has(v)) throw new Error('invalid_arguments');
    if (
      !Array.isArray(v) &&
      Object.getPrototypeOf(v) !== Object.prototype &&
      Object.getPrototypeOf(v) !== null
    )
      throw new Error('invalid_arguments');
    active.add(v);
    const result = Array.isArray(v)
      ? `[${v.map(encode).join(',')}]`
      : `{${Object.keys(v)
          .sort()
          .map((k) => {
            if (['__proto__', 'constructor', 'prototype'].includes(k))
              throw new Error('invalid_arguments');
            return `${JSON.stringify(k)}:${encode((v as Record<string, unknown>)[k])}`;
          })
          .join(',')}}`;
    active.delete(v);
    return result;
  };
  return encode(value);
}

/** Native JSON syntax parsing plus duplicate member rejection before canonical hashing. */
export function parseStrictClaudeValueLoopJSON(raw: string): unknown {
  const value: unknown = JSON.parse(raw);
  const stack: Array<Set<string> | null> = [];
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === '{' || ch === '[') {
      stack.push(ch === '{' ? new Set() : null);
      continue;
    }
    if (ch === '}' || ch === ']') {
      stack.pop();
      continue;
    }
    if (ch !== '"') continue;
    const start = i++;
    for (; i < raw.length; i++) {
      if (raw[i] === '\\') {
        i++;
        continue;
      }
      if (raw[i] === '"') break;
    }
    let next = i + 1;
    while (/\s/.test(raw[next] ?? '') && next < raw.length) next++;
    if (raw[next] === ':') {
      const key = JSON.parse(raw.slice(start, i + 1)) as string;
      const keys = stack.at(-1);
      if (!keys || keys.has(key)) throw Error('invalid_arguments');
      keys.add(key);
    }
  }
  canonicalClaudeValueLoopJSON(value);
  return value;
}

/** Native Web Crypto works in browser consumers and Node 22 without crypto reimplementation. */
export async function hashClaudeValueLoopRequest(request: {
  method: string;
  arguments: unknown;
}): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalClaudeValueLoopJSON(request));
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', bytes));
  let binary = '';
  for (const byte of digest) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export interface ClaudeValueLoopAssertionV2 {
  iss: string;
  aud: 'skillspec-api-remote-connection';
  iat: number;
  nbf: number;
  exp: number;
  jti: string;
  purpose: 'remote-connection-value-loop';
  version: 2;
  userId: number;
  workspaceId: number;
  clientId: string;
  connectionId: string;
  grantId: string;
  authorityEpochId: string;
  accessJti: string;
  profile: typeof CLAUDE_VALUE_LOOP_PROFILE;
  consentRevision: 1;
  scope: ClaudeValueLoopScope[];
  capability: ClaudeValueLoopTool;
  operation: ClaudeValueLoopOperation;
  operationKey: string | null;
  requestHash: string;
}
export interface ClaudeValueLoopReceipt {
  receiptId: string;
  operation: 'create_skill' | 'publish_version' | 'record_use';
  committedAt: string;
  replayed: boolean;
}
export interface ClaudeValueLoopPackagePreview {
  previewId: string;
  expiresAt: string;
  packageDigest: string;
  manifest: SkillPackageFileInfo[];
  missingReferences: { source: string; path: string; reason?: string }[];
  scanCoverage: SkillPackageScanCoverage[];
  publicationAllowed: boolean;
  publicationBlockers: string[];
}
export const CLAUDE_VALUE_LOOP_ERROR_CODES = [
  'invalid_arguments',
  'caller_authority_denied',
  'mixed_credentials',
  'invalid_package',
  'confirmation_required',
  'remote_assertion_required',
  'remote_assertion_invalid',
  'remote_assertion_expired',
  'scope_denied',
  'authority_denied',
  'writes_disabled',
  'origin_forbidden',
  'resource_unavailable',
  'receipt_not_found',
  'stale_version',
  'idempotency_conflict',
  'use_conflict',
  'preview_mismatch',
  'remote_assertion_replayed',
  'preview_expired',
  'payload_too_large',
  'rate_limited',
  'authority_state_unavailable',
  'service_unavailable',
] as const;
export type ClaudeValueLoopErrorCode = (typeof CLAUDE_VALUE_LOOP_ERROR_CODES)[number];
export interface ClaudeValueLoopError {
  error: ClaudeValueLoopErrorCode;
  retryable: boolean;
  receiptId?: string;
}
export interface SkillReportedUseAnalysis {
  skillId: number;
  window: { from: string; to: string; basis: 'server_received_at' };
  versions: Array<{
    versionId: number;
    digest: string;
    evidenceState: 'empty' | 'insufficient' | 'available';
    reportedUseCount: number;
    humanFeedback: {
      count: number;
      denominator: number;
      coverage: number | null;
      outcomes: Record<'success' | 'partial' | 'failure' | 'unknown', number>;
    };
    clientReportedOutcomes: Record<'success' | 'partial' | 'failure' | 'unknown', number> & {
      knownCount: number;
      denominator: number;
    };
    models: Array<{
      modelFamily: 'claude' | 'gpt' | 'gemini' | 'other' | 'unknown';
      count: number;
      source: 'client_reported';
    }>;
    cohorts: Array<{
      modelFamily: 'claude' | 'gpt' | 'gemini' | 'other' | 'unknown';
      context: 'writing' | 'coding' | 'research' | 'other' | 'unknown';
      count: number;
    }>;
    provenance: {
      source: 'reported_use';
      executionVerified: false;
      reportingCoverage: 'unknown';
      retrievalIsUse: false;
    };
    warnings: string[];
  }>;
  comparison: { comparable: boolean; reasons: string[] };
  legacyEvidence: {
    version: 'unknown';
    includedInVersionCounts: false;
    availability: 'not_queried' | 'available' | 'unavailable';
  };
  benchmarks: {
    state: 'not_available' | 'unaccepted' | 'accepted';
    runs: Array<{
      runId: number;
      benchmarkId: number;
      benchmarkVersion: number;
      status: string;
      packageVersionId: number;
      packageDigest: string;
      executionModel: string;
      scorerModel: string;
      coverage: unknown;
      score: number;
      sampleSize: number;
      acceptedEvidenceRef: string;
    }>;
    separateFromReportedUse: true;
  };
}
export interface OwnSkillReportedUse {
  id: string;
  useId: string;
  versionId: number;
  digest: string;
  receivedAt: string;
  reportedModel: string | null;
  reportedContext: string;
  reportedOutcome: string;
  withdrawnAt: string | null;
  feedbackState: 'none' | 'active' | 'withdrawn' | 'erased';
  canAddFeedback: boolean;
  feedback: { outcome: string | null; note: string | null } | null;
}

export interface OwnSkillReportedUseList {
  items: OwnSkillReportedUse[];
  limit: 50;
  possiblyMore: boolean;
  nextCursor: string | null;
  feedbackCreationEnabled: boolean;
}
export interface SkillReportedUseActionResponse {
  id: string;
  skillId: number;
  versionId: number;
  action: 'FEEDBACK_CREATE' | 'FEEDBACK_WITHDRAW' | 'USE_WITHDRAW';
  withdrawn: boolean;
  receiptId: string;
  replayed: boolean;
}
